import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import "./columnFilterPopover.scss";

function Checkbox({ state, onChange }) {
  return (
    <span
      className={`cfp__cb cfp__cb--${state}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onChange();
      }}
      role="checkbox"
      aria-checked={state === "indeterminate" ? "mixed" : state === "checked"}
    >
      {state === "checked" && (
        <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d="M4 8.2 6.5 10.5 11 6"
            stroke="white"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      {state === "indeterminate" && (
        <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <rect x="4" y="7" width="8" height="2" rx="1" fill="white" />
        </svg>
      )}
    </span>
  );
}

function ColumnFilterPopover({
  title,
  options,
  loadOptions,
  selectedIds,
  selectedItems,
  mode: initialMode,
  onApply,
  onClose,
  searchPlaceholder,
  applyLabel,
  emptyText,
  className,
  closeOnApply,
  anchorRef,
  anchorOrigin,
  transformOrigin,
  marginThreshold,
  pageSize,
}) {
  const isAsync = typeof loadOptions === "function";
  const [query, setQuery] = useState("");
  // draft is always the list of *touched* ids. Whether a row is rendered as
  // checked depends on mode: include → checked iff draft.has(id), exclude →
  // checked iff !draft.has(id). This lets "everything except these few" be
  // expressed without enumerating every id in the dataset.
  const [draft, setDraft] = useState(() => new Set(selectedIds));
  const [mode, setMode] = useState(
    initialMode === "exclude" ? "exclude" : "include",
  );

  // Label cache (id -> label). In async mode we need this to render selected
  // items whose row may not currently be in the loaded page.
  const [labels, setLabels] = useState(() => {
    const m = new Map();
    (options || []).forEach((o) => m.set(o.id, o.label));
    (selectedItems || []).forEach((it) => m.set(it.id, it.label));
    return m;
  });

  // Async-mode state
  const [asyncRows, setAsyncRows] = useState([]);
  const [asyncTotal, setAsyncTotal] = useState(0);
  const [asyncHasMore, setAsyncHasMore] = useState(false);
  const [asyncLoading, setAsyncLoading] = useState(false);
  const asyncOffsetRef = useRef(0);
  const asyncQueryRef = useRef("");
  const requestIdRef = useRef(0);

  const rootRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    setDraft(new Set(selectedIds));
  }, [selectedIds]);

  useEffect(() => {
    setMode(initialMode === "exclude" ? "exclude" : "include");
  }, [initialMode]);

  useEffect(() => {
    setLabels((prev) => {
      const next = new Map(prev);
      (options || []).forEach((o) => next.set(o.id, o.label));
      (selectedItems || []).forEach((it) => next.set(it.id, it.label));
      return next;
    });
  }, [options, selectedItems]);

  useEffect(() => {
    function handleOutside(e) {
      const inPopover = rootRef.current && rootRef.current.contains(e.target);
      const inAnchor =
        anchorRef?.current && anchorRef.current.contains(e.target);
      if (!inPopover && !inAnchor) onClose();
    }
    function handleKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [onClose, anchorRef]);

  // Async loader. Stale responses are dropped via requestIdRef.
  const loadPage = useCallback(
    async (q, offset) => {
      if (!isAsync) return;
      const myId = ++requestIdRef.current;
      setAsyncLoading(true);
      try {
        const res = await loadOptions(q, offset, pageSize);
        if (myId !== requestIdRef.current) return;
        const rows = (res?.options || []).map((o) => ({
          id: o.id,
          label: o.label,
        }));
        setAsyncRows((prev) => (offset === 0 ? rows : [...prev, ...rows]));
        setAsyncTotal(typeof res?.total === "number" ? res.total : rows.length);
        setAsyncHasMore(Boolean(res?.hasMore));
        asyncOffsetRef.current = offset + rows.length;
        setLabels((prev) => {
          const next = new Map(prev);
          rows.forEach((r) => next.set(r.id, r.label));
          return next;
        });
      } catch (err) {
        if (myId === requestIdRef.current) {
          console.error("ColumnFilterPopover loadOptions failed:", err);
        }
      } finally {
        if (myId === requestIdRef.current) setAsyncLoading(false);
      }
    },
    [isAsync, loadOptions, pageSize],
  );

  // Initial page load (async mode).
  useEffect(() => {
    if (!isAsync) return;
    asyncQueryRef.current = "";
    loadPage("", 0);
  }, [isAsync, loadPage]);

  // Debounced search re-load (async mode).
  useEffect(() => {
    if (!isAsync) return;
    const h = setTimeout(() => {
      if (asyncQueryRef.current === query) return;
      asyncQueryRef.current = query;
      loadPage(query, 0);
    }, 250);
    return () => clearTimeout(h);
  }, [query, isAsync, loadPage]);

  const handleListScroll = () => {
    if (!isAsync || asyncLoading || !asyncHasMore) return;
    const el = listRef.current;
    if (!el) return;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remaining < 80) {
      loadPage(asyncQueryRef.current, asyncOffsetRef.current);
    }
  };

  // Derive the rows visible inside the list right now.
  const visible = useMemo(() => {
    if (isAsync) {
      const seen = new Set(asyncRows.map((r) => r.id));
      const pinned = [];
      draft.forEach((id) => {
        if (!seen.has(id)) {
          pinned.push({ id, label: labels.get(id) ?? String(id) });
        }
      });
      return [...pinned, ...asyncRows];
    }
    const q = query.trim().toLowerCase();
    return q
      ? options.filter((o) => o.label.toLowerCase().includes(q))
      : options;
  }, [isAsync, asyncRows, draft, labels, query, options]);

  // Whether a given row id renders as "checked" given the current mode.
  const isRowChecked = useCallback(
    (id) => (mode === "exclude" ? !draft.has(id) : draft.has(id)),
    [mode, draft],
  );

  // With no search active, the two "whole dataset" endpoints are reachable
  // without enumeration:
  //   (exclude, draft={}) → everything selected
  //   (include, draft={}) → nothing selected
  // With a search active, the user's intent is scoped to the matching rows,
  // so we derive checkbox state from the loaded/visible scope instead — and
  // toggleAll operates on that scope rather than flipping endpoints.
  const hasSearch = query.trim().length > 0;
  const isAllEndpoint = mode === "exclude" && draft.size === 0;
  const isNoneEndpoint = mode === "include" && draft.size === 0;

  let selectAllState;
  if (!hasSearch && isAsync) {
    selectAllState = isAllEndpoint
      ? "checked"
      : isNoneEndpoint
      ? "unchecked"
      : "indeterminate";
  } else {
    // Static (any state) OR async with a search query: derive from the
    // matching rows. In static `visible` is the filtered set; in async with
    // search, `asyncRows` is the search result page (pinned items are not
    // matches, so we exclude them from the scope here).
    const scope = isAsync ? asyncRows : visible;
    const all = scope.length > 0 && scope.every((o) => isRowChecked(o.id));
    const none = scope.every((o) => !isRowChecked(o.id));
    selectAllState = all ? "checked" : none ? "unchecked" : "indeterminate";
  }

  function toggle(id) {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // No search + async: flip between dataset-wide endpoints without
  // enumeration. No search + static: same idea — the (mode, draft={})
  // endpoints work there too. With an active search the user intent is
  // scoped to the matching rows, so toggle just those (preserves the search
  // context in the resulting selection). Indeterminate snaps to "all checked"
  // (standard checkbox behavior).
  function toggleAll() {
    const wantChecked = selectAllState !== "checked";
    if (!hasSearch) {
      setMode(wantChecked ? "exclude" : "include");
      setDraft(new Set());
      return;
    }
    const scope = isAsync ? asyncRows : visible;
    setDraft((prev) => {
      const next = new Set(prev);
      scope.forEach((o) => {
        const shouldHave = mode === "exclude" ? !wantChecked : wantChecked;
        if (shouldHave) next.add(o.id);
        else next.delete(o.id);
      });
      return next;
    });
  }

  // Invert is the whole point: flip the mode. The draft (touched ids) stays
  // intact — what changes is whether those ids are "what to keep" or "what to
  // drop". Crucially, this works for datasets larger than the loaded page,
  // since no enumeration is required.
  function invert() {
    setMode((m) => (m === "exclude" ? "include" : "exclude"));
  }

  function handleApply() {
    const ids = Array.from(draft);
    const items = ids.map((id) => ({ id, label: labels.get(id) ?? String(id) }));
    onApply(ids, items, mode);
    if (closeOnApply) onClose();
  }

  // Footer count reflects the effective selection under the current mode.
  let countText;
  if (mode === "exclude") {
    if (isAsync) {
      const effective = Math.max(asyncTotal - draft.size, 0);
      countText = `${effective.toLocaleString()} selected · ${draft.size} excluded · ${asyncRows.length} of ${asyncTotal.toLocaleString()} loaded`;
    } else {
      const total = options.length;
      const effective = Math.max(total - draft.size, 0);
      countText = `${effective} selected · ${draft.size} excluded of ${total}`;
    }
  } else if (isAsync) {
    countText = `${draft.size} selected · ${asyncRows.length} of ${asyncTotal.toLocaleString()} loaded`;
  } else {
    const total = options.length;
    countText =
      draft.size === total
        ? `${total} selected`
        : `${draft.size} of ${total} selected`;
  }

  const useAnchoredPlacement = !!anchorRef;
  const [coords, setCoords] = useState({ top: 0, left: 0, ready: false });

  useLayoutEffect(() => {
    if (!useAnchoredPlacement) return;

    const computeCoords = () => {
      const anchorEl = anchorRef.current;
      const popoverEl = rootRef.current;
      if (!anchorEl || !popoverEl) return;

      const anchorRect = anchorEl.getBoundingClientRect();
      const popoverWidth = popoverEl.offsetWidth;
      const popoverHeight = popoverEl.offsetHeight;

      const aX =
        anchorOrigin.horizontal === "center"
          ? anchorRect.left + anchorRect.width / 2
          : anchorOrigin.horizontal === "right"
          ? anchorRect.right
          : anchorRect.left;
      const aY =
        anchorOrigin.vertical === "center"
          ? anchorRect.top + anchorRect.height / 2
          : anchorOrigin.vertical === "bottom"
          ? anchorRect.bottom
          : anchorRect.top;

      const tX =
        transformOrigin.horizontal === "center"
          ? popoverWidth / 2
          : transformOrigin.horizontal === "right"
          ? popoverWidth
          : 0;
      const tY =
        transformOrigin.vertical === "center"
          ? popoverHeight / 2
          : transformOrigin.vertical === "bottom"
          ? popoverHeight
          : 0;

      // Viewport coordinates — used with position: fixed so the popover
      // tracks a sticky anchor through page scroll.
      let top = aY - tY;
      let left = aX - tX;

      // Clamp to viewport with marginThreshold.
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      const minTop = marginThreshold;
      const maxTop = vh - popoverHeight - marginThreshold;
      const minLeft = marginThreshold;
      const maxLeft = vw - popoverWidth - marginThreshold;
      if (maxTop >= minTop) top = Math.min(Math.max(top, minTop), maxTop);
      if (maxLeft >= minLeft) left = Math.min(Math.max(left, minLeft), maxLeft);

      setCoords({ top, left, ready: true });
    };

    computeCoords();
    window.addEventListener("resize", computeCoords);
    return () => {
      window.removeEventListener("resize", computeCoords);
    };
  }, [
    useAnchoredPlacement,
    anchorRef,
    anchorOrigin,
    transformOrigin,
    marginThreshold,
  ]);

  const anchoredStyle = useAnchoredPlacement
    ? {
        position: "fixed",
        top: coords.top,
        left: coords.left,
        visibility: coords.ready ? "visible" : "hidden",
        zIndex: 1100,
      }
    : undefined;

  const popoverNode = (
    <div
      ref={rootRef}
      className={`cfp${className ? ` ${className}` : ""}`}
      style={anchoredStyle}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="cfp__header">
        <span className="cfp__title">{title}</span>
        <button
          type="button"
          className="cfp__close"
          onClick={onClose}
          aria-label="Close filter"
        >
          <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M4 4l8 8M12 4l-8 8"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      <div className="cfp__search">
        <svg
          className="cfp__search-icon"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="7"
            cy="7"
            r="5"
            stroke="currentColor"
            strokeWidth="1.7"
          />
          <path
            d="M11 11l3 3"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
          />
        </svg>
        <input
          type="text"
          placeholder={searchPlaceholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <div className="cfp__select-all">
        <Checkbox state={selectAllState} onChange={toggleAll} />
        <span className="cfp__label" onClick={toggleAll}>Select All</span>
        <button
          type="button"
          className="cfp__invert"
          onClick={invert}
          title="Invert selection — what was selected becomes deselected and vice versa, across the entire dataset"
        >
          Invert
        </button>
      </div>

      <ul
        className="cfp__list"
        ref={listRef}
        onScroll={handleListScroll}
      >
        {visible.length === 0 && !asyncLoading && (
          <li className="cfp__empty">{emptyText}</li>
        )}
        {visible.map((opt) => {
          const checked = isRowChecked(opt.id);
          return (
            <li
              key={opt.id}
              className="cfp__row"
              onClick={() => toggle(opt.id)}
            >
              <Checkbox
                state={checked ? "checked" : "unchecked"}
                onChange={() => toggle(opt.id)}
              />
              <span className="cfp__label">{opt.label}</span>
              {opt.count != null && (
                <span className="cfp__count-badge">{opt.count}</span>
              )}
            </li>
          );
        })}
        {isAsync && asyncLoading && (
          <li className="cfp__loading">Loading...</li>
        )}
      </ul>

      <div className="cfp__footer">
        <span className="cfp__count">{countText}</span>
        <button type="button" className="cfp__apply" onClick={handleApply}>
          {applyLabel}
        </button>
      </div>
    </div>
  );

  if (useAnchoredPlacement) {
    return ReactDOM.createPortal(popoverNode, document.body);
  }
  return popoverNode;
}

ColumnFilterPopover.propTypes = {
  title: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  loadOptions: PropTypes.func,
  selectedIds: PropTypes.array,
  selectedItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  mode: PropTypes.oneOf(["include", "exclude"]),
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  searchPlaceholder: PropTypes.string,
  applyLabel: PropTypes.string,
  emptyText: PropTypes.string,
  className: PropTypes.string,
  closeOnApply: PropTypes.bool,
  anchorRef: PropTypes.shape({ current: PropTypes.any }),
  anchorOrigin: PropTypes.shape({
    vertical: PropTypes.oneOf(["top", "center", "bottom"]),
    horizontal: PropTypes.oneOf(["left", "center", "right"]),
  }),
  transformOrigin: PropTypes.shape({
    vertical: PropTypes.oneOf(["top", "center", "bottom"]),
    horizontal: PropTypes.oneOf(["left", "center", "right"]),
  }),
  marginThreshold: PropTypes.number,
  pageSize: PropTypes.number,
};

ColumnFilterPopover.defaultProps = {
  options: [],
  loadOptions: null,
  selectedIds: [],
  selectedItems: [],
  mode: "include",
  searchPlaceholder: "Search...",
  applyLabel: "Apply Filter",
  emptyText: "No matches found",
  className: "",
  closeOnApply: true,
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
  marginThreshold: 8,
  pageSize: 50,
};

export default ColumnFilterPopover;
